import {useState, Dispatch, SetStateAction, useMemo} from 'react';
import './activities-group-entry.scss';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {CarePlanActivitiesGroup} from '../../../backend/interfaces/care-plan-activities-group';
import {useAuth} from '../../../contexts/auth/context';
import {EntityUserCarePlansRepository} from '../../../repositories/entity-user-care-plans';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {useSnackBar} from '../../../contexts/snack-bar/context';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {ConfigurationsValuesIndex} from '../../../types';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {EditActivitiesGroupDialog} from '../edit-activities-group-dialog/edit-activities-group-dialog';
import {DeleteActivityGroupEntryConfirmation} from '../delete-activity-group-entry-confirmation/delete-activity-group-entry-confirmation';
import {AddActivityEntryDialog} from '../add-activity-type-entry-dialog/add-activity-type-entry-dialog';
import {EditActivityEntryDialog} from '../edit-activity-type-entry-dialog/edit-activity-type-entry-dialog';
import {ViewActivityEntryDialog} from '../view-activity-type-entry-dialog/view-activity-type-entry-dialog';
import {ActivitiesGroupCard} from '../../../screens/private/office/professional-caregiver/settings/templates/activities-group-card';

interface CarePlanSheetActivitiesGroupEntryProps {
  seniorUserId: string;
  activitiesSectorId: string;
  entry: CarePlanActivitiesGroup;
  onUpdate: (entry: CarePlanActivitiesGroup) => void;
  onDelete: () => void;
  onEditMode: boolean;
  setEditMode: Dispatch<SetStateAction<string | undefined>>;
}

export const CarePlanSheetActivitiesGroupEntry = ({
  seniorUserId,
  activitiesSectorId,
  entry,
  onUpdate,
  onDelete,
  onEditMode,
  setEditMode,
}: CarePlanSheetActivitiesGroupEntryProps) => {
  const {t} = useTranslation();
  const authContext = useAuth();
  const snackBarContext = useSnackBar();
  const [editActivityGroup, setEditActivityGroup] = useState(false);
  const [confirmActivityGroupDeletion, setConfirmActivityGroupDeletion] = useState<string>();
  const [activityEntryEditType, setActivityEntryEditType] = useState<
    'create' | 'edit' | 'view' | undefined
  >(undefined);
  const [selectedActivityGroupTypeEntry, setSelectedActivityGroupTypeEntry] = useState<
    CarePlanActivitiesGroupType | undefined
  >(undefined);
  const [selectedActivityGroupTypeId, setSelectedActivityGroupTypeId] = useState<
    string | undefined
  >(undefined);
  const [configurationsValuesIndex, setConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);

  const handleUpdateActivity = async (
    updatedConfigurations: Array<CarePlanActivitiesGroupApiRequestConfigurationsItem>
  ) => {
    EntityUserCarePlansRepository.instance
      .updateActivitiesGroupOnCarePlan(
        authContext.state.session?.selectedAffiliation?.entity.id ?? '',
        seniorUserId,
        activitiesSectorId,
        entry.id ?? '',
        {
          activitiesGroupId: entry.activitiesGroupId,
          configurations: updatedConfigurations,
        }
      )
      .then((response) => {
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.activities_group_entry.snackbar.update.success),
        });

        // Notify
        onUpdate(response);
      })
      .catch((error) => {
        // Show Toast
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.activities_group_entry.snackbar.update.error),
        });
      });
  };

  // FIX-ME: Temporary - this is here to enable us to search the `CarePlanActivitiesGroup` configurations for the defined group repetition config.
  // That should probably be comming directly on the fields configs per record, inside the dynamic form `configurationsIndex`
  const groupRepetitionConfigurationType = useMemo(() => {
    let type: 'activities-group' | 'activity-type' | undefined = undefined;

    if (entry.configurations) {
      for (const item of entry.configurations) {
        try {
          const jsonValue = JSON.parse(item.carePlanConfigurationValue);
          if (typeof jsonValue === 'object' && jsonValue.hasOwnProperty('type'))
            type = jsonValue.type;
        } catch (error) {
          // Ignore non-parseable JSON values
        }
      }
    }

    return type;
  }, [entry.configurations]);

  return (
    <>
      <ActivitiesGroupCard
        activitiesSectorId={activitiesSectorId}
        activitiesGroupId={entry.activitiesGroupId}
        activitiesGroupConfigurations={entry.configurations}
        activityTypes={entry.activityTypes}
        id={entry.id}
        editMode={onEditMode}
        setEditMode={setEditMode}
        onEditActivityGroup={(defaultValues: ConfigurationsValuesIndex) => {
          setConfigurationsValuesIndex(defaultValues);
          setEditActivityGroup(true);
        }}
        onOpenCreateActivityGroupEntry={(activityType: ActivityType) => {
          setActivityEntryEditType('create');
          setSelectedActivityGroupTypeId(activityType.id);
        }}
        onOpenEditActivityGroupEntry={(activitiesGroupType: CarePlanActivitiesGroupType) => {
          setActivityEntryEditType('edit');
          setSelectedActivityGroupTypeEntry(activitiesGroupType);
        }}
        onOpenViewActivityGroupEntry={(activitiesGroupType) => {
          setActivityEntryEditType('view');
          setSelectedActivityGroupTypeEntry(activitiesGroupType);
        }}
        onDeleteActivityGroupEntry={setConfirmActivityGroupDeletion}
      />

      {/* MODAL FOR EDITING ACTIVITY GROUP */}
      {onEditMode && editActivityGroup && (
        <EditActivitiesGroupDialog
          open={editActivityGroup}
          onClose={() => setEditActivityGroup(false)}
          activitiesSectorId={activitiesSectorId}
          activitiesGroupId={entry.activitiesGroupId}
          onUpdate={handleUpdateActivity}
          defaultValues={configurationsValuesIndex}
        />
      )}

      {/* MODAL FOR CONFIRMING DELETE ACTIVITY GROUP ENTRY */}
      {confirmActivityGroupDeletion && entry.id && (
        <DeleteActivityGroupEntryConfirmation
          open={confirmActivityGroupDeletion !== undefined}
          onClose={() => setConfirmActivityGroupDeletion(undefined)}
          seniorUserId={seniorUserId}
          activitiesSectorId={activitiesSectorId}
          entryId={entry.id}
          activitiesGroupName={confirmActivityGroupDeletion}
          onDelete={onDelete}
        />
      )}

      {/* MODAL FOR EDITING ACTIVITY GROUP TASK ENTRY */}
      {onEditMode && selectedActivityGroupTypeEntry && entry.id && (
        <EditActivityEntryDialog
          open={activityEntryEditType === 'edit'}
          onClose={() => setActivityEntryEditType(undefined)}
          seniorUserId={seniorUserId}
          activitiesSectorId={activitiesSectorId}
          activityGroupEntry={entry}
          entry={selectedActivityGroupTypeEntry}
          onUpdate={onUpdate}
          repetitionType={groupRepetitionConfigurationType}
        />
      )}

      {/* MODAL FOR VIEWING ACTIVITY GROUP TASK ENTRY */}
      {selectedActivityGroupTypeEntry && entry.id && (
        <ViewActivityEntryDialog
          open={activityEntryEditType === 'view'}
          onClose={() => setActivityEntryEditType(undefined)}
          activitiesSectorId={activitiesSectorId}
          activityGroupEntry={entry}
          entry={selectedActivityGroupTypeEntry}
          onUpdate={onUpdate}
          repetitionType={groupRepetitionConfigurationType}
        />
      )}

      {/* MODAL FOR ADDING/CREATING ACTIVITY GROUP TASK ENTRY */}
      {onEditMode && selectedActivityGroupTypeId && entry.id && (
        <AddActivityEntryDialog
          open={activityEntryEditType === 'create'}
          onClose={() => setActivityEntryEditType(undefined)}
          seniorUserId={seniorUserId}
          activitiesSectorId={activitiesSectorId}
          entry={entry}
          activityGroupTypeId={selectedActivityGroupTypeId}
          onUpdate={onUpdate}
          repetitionType={groupRepetitionConfigurationType}
        />
      )}
    </>
  );
};
